import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import cashboxService from '../services/cashboxService';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-hot-toast';
import { Wallet, RefreshCw } from 'lucide-react';

const CashboxMyTransactions = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 20;

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ['cashbox-my-transactions', currentPage],
        queryFn: () => cashboxService.getTransactions({ page: currentPage, limit: pageSize })
    });

    const transactions = data?.data?.transactions || [];
    const pagination = data?.data?.pagination || { page: 1, pages: 1, total: 0 };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value || 0);
    };

    const getTypeBadge = (type) => {
        const badges = {
            INCOME: 'bg-green-100 text-green-700',
            EXPENSE: 'bg-red-100 text-red-700',
            INVESTMENT: 'bg-blue-100 text-blue-700',
            INVESTMENT_RETURN: 'bg-purple-100 text-purple-700',
            TRANSFER_IN: 'bg-cyan-100 text-cyan-700',
            TRANSFER_OUT: 'bg-orange-100 text-orange-700'
        };
        return badges[type] || 'bg-gray-100 text-gray-700';
    };

    const getTypeLabel = (type) => {
        const labels = {
            INCOME: 'Entrada',
            EXPENSE: 'Saída',
            INVESTMENT: 'Investimento',
            INVESTMENT_RETURN: 'Retorno',
            TRANSFER_IN: 'Transferência Entrada',
            TRANSFER_OUT: 'Transferência Saída'
        };
        return labels[type] || type;
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-600 mb-4">Erro ao carregar transações</p>
                    <button onClick={() => refetch()} className="btn-primary">
                        Tentar novamente
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
                    <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary-100 rounded-lg">
                                <Wallet className="h-5 w-5 text-primary-600" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">Minhas Transações</h2>
                                <p className="text-sm text-gray-500">{pagination.total} transações encontradas</p>
                            </div>
                        </div>
                        <button
                            onClick={() => refetch()}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                            title="Atualizar"
                        >
                            <RefreshCw className="h-5 w-5 text-gray-500" />
                        </button>
                    </div>

                    {transactions.length === 0 ? (
                        <div className="p-12 text-center">
                            <Wallet className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500">Nenhuma transação encontrada</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Valor</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {transactions.map((tx) => (
                                        <tr key={tx.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm text-gray-600">
                                                {format(new Date(tx.reference_date), 'dd/MM/yyyy', { locale: ptBR })}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                {tx.description}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600">
                                                {tx.category_name || '-'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeBadge(tx.transaction_type)}`}>
                                                    {getTypeLabel(tx.transaction_type)}
                                                </span>
                                            </td>
                                            <td className={`px-4 py-3 text-sm font-medium text-right ${
                                                ['INCOME', 'INVESTMENT', 'INVESTMENT_RETURN', 'TRANSFER_IN'].includes(tx.transaction_type)
                                                    ? 'text-green-600'
                                                    : 'text-red-600'
                                            }`}>
                                                {['INCOME', 'INVESTMENT', 'INVESTMENT_RETURN', 'TRANSFER_IN'].includes(tx.transaction_type)
                                                    ? '+'
                                                    : '-'}
                                                {formatCurrency(tx.amount)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {pagination.pages > 1 && (
                        <div className="px-6 py-4 border-t flex items-center justify-between">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 border rounded disabled:opacity-50"
                            >
                                Anterior
                            </button>
                            <span className="text-sm text-gray-600">
                                Página {currentPage} de {pagination.pages}
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(pagination.pages, p + 1))}
                                disabled={currentPage === pagination.pages}
                                className="px-3 py-1 border rounded disabled:opacity-50"
                            >
                                Próxima
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CashboxMyTransactions;
